import { Button, CircularProgress, Grid, Typography } from '@mui/material';

import ActionButton from 'common/components/ActionButton';
import useStylesForm from 'common/components/styles/useStylesForm.styles';
import YesNoDialog from 'common/components/YesNoDialog';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductsOptionsAction, getProductsOptions, getProductsSavePending } from 'redux/slices/productSlice';
import useStylesProductForm from '../styles/useStylesProductForm.styles';
import ProductFormAddVariants from './ProductFormAddVariants';
import ProductFormOptions from './ProductFormOptions';
import ProductFormVariantsRow from './ProductFormVariantsRow';

const ProductFormVersions = (props) => {
    const {
        fields,
        onSubmit,
        cancel,
        variants,
        options,
        setVariants,
        setOptions,
        readOnly,
        step,
        setStep,
        formConfigProductVariant,
        canEdit,
    } = props;

    const { classes: classes1 } = useStylesForm();
    const { classes: classes2, cx } = useStylesProductForm();
    const classes = { ...classes1, ...classes2 };

    const { t } = useTranslation();

    const productsOptions = useSelector(getProductsOptions);

    const savePending = useSelector(getProductsSavePending);

    const { enqueueSnackbar } = useSnackbar();

    const dispatch = useDispatch();

    const [showForm, setShowForm] = useState(false);

    const [editingVariantIndex, setEditingVariantIndex] = useState(-1);

    const [openConfirmDeleteDlg, setOpenConfirmDeleteDlg] = useState(false);

    const [deleteIdx, setDeleteIdx] = useState(-1);

    const deleteConfirmTitle = t('product.productFormVersions.deleteConfirm');
    const deleteConfirmMessage = t('product.productFormVersions.deleteMessage');

    const validateForm = () => {
        return true; // TODO
    };

    useEffect(() => {
        dispatch(fetchProductsOptionsAction());
    }, [dispatch]);

    const variantCreate = {
        url: '',
        images: [],
        currency: fields.currency,
        price: undefined,
        // discountType: 'none',
        // discount: undefined,
        stock: undefined,
        selectedOptions: [],
    };

    const handleDelete = (idx) => {
        setDeleteIdx(idx);
        setOpenConfirmDeleteDlg(true);
    };

    const confirmDelete = () => {
        deleteVariant(deleteIdx);
        setOpenConfirmDeleteDlg(false);
    };

    const cancelDlg = () => {
        setOpenConfirmDeleteDlg(false);
    };

    const deleteVariant = (idx) => {
        let newArr = [...variants];
        newArr.splice(idx, 1);
        setVariants(newArr);
    };

    const setNewVariant = (variant) => {
        setVariants([...variants, variant]);
    };

    const setEditedVariant = (variant, idx) => {
        let newArr = [...variants];
        newArr[idx] = variant;
        setVariants(newArr);
    };

    const someOptionsAvailable = () => {
        for (const option of options) {
            if (option.name && option.values.length > 0) {
                return true;
            }
        }
        return false;
    };

    const submit = () => {
        if (options.length === 0) {
            enqueueSnackbar(t('product.productFormVersions.optionError'), {
                variant: 'error',
            });
        } else if (variants.length === 0) {
            enqueueSnackbar(t('product.productFormVersions.variantError'), {
                variant: 'error',
            });
        } else if (showForm || editingVariantIndex !== -1) {
            enqueueSnackbar(t('product.productFormVersions.variantOpenError'), {
                variant: 'error',
            });
        } else if (checkOptionsVariantsEmpty()) {
            enqueueSnackbar(t('product.productFormVersions.variantWithoutOptionError'), {
                variant: 'error',
            });
        } else if (checkForDuplicates()) {
            enqueueSnackbar(t('product.productFormVersions.duplicateOptionError'), {
                variant: 'error',
            });
        } else {
            onSubmit();
        }
    };

    const checkOptionsVariantsEmpty = () => {
        let index = variants.findIndex((v) => v.selectedOptions.length === 0);
        return index !== -1;
    };

    const checkForDuplicates = () => {
        return new Set(options.map((item) => item.name)).size !== options.length;
    };

    return (
        <React.Fragment>
            <div className={classes.formArea}>
                <div className={classes.scroll}>
                    <ProductFormOptions
                        readOnly={readOnly}
                        productsOptions={productsOptions}
                        options={options}
                        setOptions={setOptions}
                        variants={variants}
                        setVariants={setVariants}
                    />

                    {someOptionsAvailable() && !readOnly && (
                        <ProductFormAddVariants
                            readOnly={readOnly}
                            duplicateOptions={checkForDuplicates()}
                            variants={variants}
                            setVariants={setNewVariant}
                            editingVariantIndex={editingVariantIndex}
                            options={options}
                            variant={variantCreate}
                            closeForm={(i) => setEditingVariantIndex(i)}
                            formConfigProductVariant={formConfigProductVariant}
                            canEdit={canEdit}
                        />
                    )}

                    {variants.length > 0 && (
                        <div>
                            <div className={cx(classes.actorItem, classes.mlr10px)}>
                                <Grid container spacing={1} className={classes.optionsRow}>
                                    <Grid item md={6}>
                                        <Typography>{t('product.productFormVersions.variants')}</Typography>
                                    </Grid>
                                    <Grid item md={2}>
                                        <Typography>{t('product.productFormVersions.price')}</Typography>
                                    </Grid>
                                    <Grid item md={2}>
                                        <Typography>{t('product.productFormVersions.availability')}</Typography>
                                    </Grid>
                                </Grid>
                            </div>

                            {variants.map((variant, idx) => (
                                <div key={idx} className={classes.pb10px}>
                                    <ProductFormVariantsRow
                                        readOnly={readOnly}
                                        index={idx}
                                        editingVariantIndex={editingVariantIndex}
                                        variant={variant}
                                        variants={variants}
                                        options={options}
                                        setVariants={(e) => setEditedVariant(e, idx)}
                                        deleteVariant={() => handleDelete(idx)}
                                        openForm={(i) => {
                                            setEditingVariantIndex(i);
                                            setShowForm(false);
                                        }}
                                        formConfigProductVariant={formConfigProductVariant}
                                        canEdit={canEdit}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className={classes.formButtons}>
                <div className={classes.bottomContent}>
                    <div className={classes.actions}>
                        <Button
                            variant="contained"
                            size="small"
                            disableElevation={true}
                            className={classes.cancelButton}
                            onClick={cancel}
                            disabled={savePending}
                        >
                            <span>
                                {readOnly ? t('product.productForm.close') : t('product.productFormVersions.cancel')}
                            </span>
                        </Button>
                        {readOnly && (
                            <ActionButton
                                color="primary"
                                disableElevation={true}
                                className={classes.proceedButton}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setStep(step - 1);
                                }}
                            >
                                <span>{t('product.productForm.back')}</span>
                            </ActionButton>
                        )}
                        {!readOnly && (
                            <ActionButton
                                id="onlive-button-cart-shipping-save"
                                type="submit"
                                disableElevation={true}
                                className={classes.proceedButton}
                                onClick={(e) => {
                                    e.preventDefault();
                                    submit();
                                }}
                                disabled={savePending && !validateForm()}
                            >
                                {savePending && (
                                    <div className={classes.spinner}>
                                        <CircularProgress />
                                    </div>
                                )}
                                <span>{t('product.productFormVersions.save')}</span>
                            </ActionButton>
                        )}
                    </div>
                </div>
            </div>

            <YesNoDialog
                openDialog={openConfirmDeleteDlg}
                title={deleteConfirmTitle}
                messageToConfirm={deleteConfirmMessage}
                yesButtonCallback={() => {
                    confirmDelete();
                }}
                noButtonCallback={() => {
                    cancelDlg();
                }}
            />
        </React.Fragment>
    );
};

export default ProductFormVersions;
